import { useCallback } from 'react'

import { ItemType, UploadableFileItem } from '@inno-sdks/files'
import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { initFilesSdk } from '../../api/filesSdk'
import { getAppFolderId } from '../../api/getAppFolderId'
import { useUploaderStore } from '../../store/uploadStore'

export const useUploadFiles = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.file_upload'
  })
  const {
    addItem,
    startUpload,
    updateProgress,
    markCompleted,
    markFailed,
    removeItem
  } = useUploaderStore()

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const { filesSdk, success } = initFilesSdk()

      if (!success) {
        toast.error({
          title: t('upload_failed'),
          subTitle: t('not_logged_in')
        })
        return
      }

      const abortController = new AbortController()

      // Add items to the upload store
      const uploadIds = files.map(file => {
        const id = `upload-${Date.now()}-${file.name}`
        addItem({
          id,
          fileName: file.name,
          progress: 0,
          status: 'pending',
          abortController
        })
        return id
      })

      try {
        if (!filesSdk) return

        const folderId = await getAppFolderId()
        if (folderId == null) return

        const uploadableItems: UploadableFileItem[] = files.map(file => ({
          type: ItemType.File,
          file,
          name: file.name,
          metaData: {
            size: file.size,
            mimeType: file.type || 'application/octet-stream'
          }
        }))

        // Mark all items as uploading before starting
        uploadIds.forEach(id => startUpload(id))

        await filesSdk.items.create(uploadableItems, folderId, {
          onProgress: progress => {
            // Update progress for all items in this batch (progress is 0-100)
            uploadIds.forEach(id => updateProgress(id, Math.round(progress)))
          },
          abortController
        })

        // Mark all items as completed
        uploadIds.forEach(id => markCompleted(id))

        // Remove completed items after a delay
        setTimeout(() => {
          uploadIds.forEach(id => removeItem(id))
          // Invalidate the files list to refresh the grid
          queryClient.invalidateQueries({ queryKey: ['files-list'] })
        }, 2000)
      } catch (error) {
        // Mark all items as failed
        uploadIds.forEach(id => markFailed(id))

        // Remove failed items after a delay so user can see the error
        setTimeout(() => {
          uploadIds.forEach(id => removeItem(id))
        }, 3000)

        // If not aborted, show error toast
        if (error instanceof Error && error.name !== 'AbortError') {
          toast.error({
            title: t('upload_failed'),
            subTitle: error.message || t('unknown_error')
          })
        }
      }
    },
    [
      queryClient,
      t,
      addItem,
      startUpload,
      updateProgress,
      markCompleted,
      markFailed,
      removeItem
    ]
  )

  return uploadFiles
}
